import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import { AuthProvider } from './contexts/AuthContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { initAnalytics } from './lib/analytics';

// Chunk-load retry: after a deploy, users with an open tab can hit a 404 (or
// a "text/html is not a valid MIME type" error from the SPA fallback) on the
// next React.lazy() import because the chunk hash changed. One reload grabs
// the new manifest. sessionStorage guard prevents loops.
const CHUNK_RELOAD_KEY = 'chunk-reload-attempt';
function handleChunkError(reason?: string) {
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  console.warn('[chunk-reload] reloading after stale-chunk error:', reason);
  window.location.reload();
}
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  handleChunkError('vite:preloadError');
});
window.addEventListener('unhandledrejection', (event) => {
  const msg = typeof event.reason === 'object' && event.reason !== null ? String(event.reason.message || '') : '';
  if (/Failed to fetch dynamically imported module|Loading chunk \d+ failed|ChunkLoadError|is not a valid JavaScript MIME type|Importing a module script failed/i.test(msg)) {
    event.preventDefault();
    handleChunkError(msg);
  }
});
window.addEventListener('error', (event) => {
  const target = event.target as HTMLElement | null;
  if (target instanceof HTMLLinkElement && target.rel === 'stylesheet' && /\/assets\//.test(target.href)) {
    handleChunkError(`stylesheet load failed: ${target.href}`);
  }
}, true);
window.addEventListener('load', () => {
  setTimeout(() => sessionStorage.removeItem(CHUNK_RELOAD_KEY), 5000);
});

initAnalytics();

// Lazy load the main App component
const App = lazy(() => import('./App'));

// Loading component
const LoadingApp = () => (
  <div className="flex h-screen items-center justify-center bg-neutral-900">
    <div className="flex flex-col items-center">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-r-transparent"></div>
      <p className="mt-4 text-white/70">Loading application...</p>
    </div>
  </div>
);

const container = document.getElementById('root')!;

// Reuse existing root on Vite HMR to prevent "already passed to createRoot" warning
const root = (container as any).__reactRoot ??= createRoot(container);

root.render(
  <StrictMode>
    <HelmetProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <AnalyticsProvider>
            <Suspense fallback={<LoadingApp />}>
              <App />
            </Suspense>
          </AnalyticsProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  </StrictMode>
);